/**
 * Lab Tab - ElevenLabs TTS integration for generating audio.
 * Supports Structural Segment (single English) and Dialogue Categories (multiple, alternating EN/FA).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FiZap,
  FiPlus,
  FiTrash2,
  FiRefreshCw,
  FiMusic,
  FiCheck,
  FiEdit2,
} from 'react-icons/fi';
import { labAPI, LabVoice, AudioFile, adminDialogueCategoriesAPI, DialogueCategory } from '../services/api';
import AudioContentEditor from './AudioContentEditor';
import './LabTab.css';

const LabTab: React.FC = () => {
  const [outputType, setOutputType] = useState<'segment' | 'dialogue'>('segment');
  const [voices, setVoices] = useState<LabVoice[]>([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [labItems, setLabItems] = useState<AudioFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Structural Segment state
  const [segmentText, setSegmentText] = useState('');
  const [segmentName, setSegmentName] = useState('');
  const [segmentPreviewB64, setSegmentPreviewB64] = useState<string | null>(null);
  const [segmentGenerating, setSegmentGenerating] = useState(false);
  const [segmentSaving, setSegmentSaving] = useState(false);

  // Dialogue state
  const [dialogueEntries, setDialogueEntries] = useState<{ text: string; lang: 'en' | 'fa' }[]>([]);
  const [dialogueFirstLang, setDialogueFirstLang] = useState<'en' | 'fa'>('en');
  const [dialoguePreviews, setDialoguePreviews] = useState<Map<number, string>>(new Map());
  const [dialogueGenerating, setDialogueGenerating] = useState<number | null>(null);
  const [dialogueSaving, setDialogueSaving] = useState(false);
  const [dialogueCategories, setDialogueCategories] = useState<DialogueCategory[]>([]);
  const [categoryDialogueName, setCategoryDialogueName] = useState('');
  const [dialogueCategoryId, setDialogueCategoryId] = useState<number | ''>('');
  const [editingContentAudioId, setEditingContentAudioId] = useState<number | null>(null);

  const fetchVoices = useCallback(async () => {
    setLoadingVoices(true);
    setError(null);
    try {
      const res = await labAPI.getVoices();
      setVoices(res.voices || []);
      if (res.voices?.length && !selectedVoiceId) {
        setSelectedVoiceId(res.voices[0].voice_id);
      }
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to load voices');
    } finally {
      setLoadingVoices(false);
    }
  }, [selectedVoiceId]);

  const fetchLabItems = useCallback(async () => {
    try {
      const items = await labAPI.getItems();
      setLabItems(items);
    } catch (err: unknown) {
      console.error('Failed to load lab items:', err);
    }
  }, []);

  const fetchDialogueCategories = useCallback(async () => {
    try {
      const cats = await adminDialogueCategoriesAPI.getCategories();
      setDialogueCategories(cats);
      if (cats.length > 0) {
        setDialogueCategoryId((prev) => (prev === '' ? cats[0].id : prev));
      }
    } catch (err: unknown) {
      console.error('Failed to load dialogue categories:', err);
    }
  }, []);

  useEffect(() => {
    fetchVoices();
    fetchLabItems();
  }, [fetchVoices, fetchLabItems]);

  useEffect(() => {
    if (outputType === 'dialogue') {
      fetchDialogueCategories();
    }
  }, [outputType, fetchDialogueCategories]);

  const base64ToBlobUrl = (b64: string): string => {
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'audio/mpeg' });
    return URL.createObjectURL(blob);
  };

  const handleGenerateSegment = async () => {
    if (!segmentText.trim() || !selectedVoiceId) return;
    setSegmentGenerating(true);
    setError(null);
    setSegmentPreviewB64(null);
    try {
      const res = await labAPI.generate({
        text: segmentText.trim(),
        voice_id: selectedVoiceId,
        language_code: 'en',
      });
      setSegmentPreviewB64(res.audio_base64);
    } catch (err: unknown) {
      setError((err as Error).message || 'TTS generation failed');
    } finally {
      setSegmentGenerating(false);
    }
  };

  const handleAddSegmentToLab = async () => {
    if (!segmentPreviewB64 || !segmentName.trim()) return;
    setSegmentSaving(true);
    setError(null);
    try {
      await labAPI.save({
        audio_base64: segmentPreviewB64,
        file_name: segmentName.trim().replace(/\s+/g, '_') + '.mp3',
      });
      setSuccess('Added to Lab successfully!');
      setSegmentPreviewB64(null);
      setSegmentName('');
      setSegmentText('');
      fetchLabItems();
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to save');
    } finally {
      setSegmentSaving(false);
    }
  };

  const addDialogueEntry = () => {
    const lang = dialogueEntries.length % 2 === 0 ? dialogueFirstLang : (dialogueFirstLang === 'en' ? 'fa' : 'en');
    setDialogueEntries([...dialogueEntries, { text: '', lang }]);
  };

  const removeDialogueEntry = (idx: number) => {
    setDialogueEntries((prev) => prev.filter((_, i) => i !== idx));
    setDialoguePreviews((prev) => {
      const next = new Map<number, string>();
      prev.forEach((v, i) => {
        if (i < idx) next.set(i, v);
        else if (i > idx) next.set(i - 1, v);
      });
      return next;
    });
  };

  const updateDialogueEntry = (idx: number, text: string) => {
    const lang = idx % 2 === 0 ? dialogueFirstLang : (dialogueFirstLang === 'en' ? 'fa' : 'en');
    setDialogueEntries((prev) => {
      const next = [...prev];
      next[idx] = { text, lang };
      return next;
    });
  };

  useEffect(() => {
    setDialogueEntries((prev) =>
      prev.map((e, i) => ({
        ...e,
        lang: i % 2 === 0 ? dialogueFirstLang : (dialogueFirstLang === 'en' ? 'fa' : 'en'),
      }))
    );
  }, [dialogueFirstLang]);

  const handleGenerateDialogueEntry = async (idx: number) => {
    const entry = dialogueEntries[idx];
    if (!entry?.text.trim() || !selectedVoiceId) return;
    setDialogueGenerating(idx);
    setError(null);
    try {
      const res = await labAPI.generate({
        text: entry.text.trim(),
        voice_id: selectedVoiceId,
        language_code: entry.lang === 'en' ? 'en' : 'fa',
      });
      setDialoguePreviews((prev) => new Map(prev).set(idx, res.audio_base64));
    } catch (err: unknown) {
      setError((err as Error).message || 'TTS generation failed');
    } finally {
      setDialogueGenerating(null);
    }
  };

  const handleAddFullDialogueToLab = async () => {
    if (dialogueEntries.length === 0) return;
    const allGenerated = dialogueEntries.every((_, idx) => dialoguePreviews.has(idx));
    if (!allGenerated) {
      setError('Generate all entries first');
      return;
    }
    const catId = typeof dialogueCategoryId === 'number' ? dialogueCategoryId : null;
    if (!catId || !categoryDialogueName.trim()) {
      setError('Select a category and enter dialogue name');
      return;
    }
    setDialogueSaving(true);
    setError(null);
    try {
      const audioIds: number[] = [];
      for (let i = 0; i < dialogueEntries.length; i++) {
        const b64 = dialoguePreviews.get(i);
        if (!b64) continue;
        const entry = dialogueEntries[i];
        const name = `dialogue_${i + 1}_${entry.lang}`.replace(/\s+/g, '_') + '.mp3';
        const saved = await labAPI.save({ audio_base64: b64, file_name: name });
        audioIds.push(saved.id);
      }
      const dialogue = await adminDialogueCategoriesAPI.createDialogue(catId, {
        category: catId,
        name: categoryDialogueName.trim(),
        is_active: true,
      });
      const fullDialogue = await adminDialogueCategoriesAPI.getDialogue(dialogue.id);
      const firstPart = fullDialogue.parts?.[0];
      if (!firstPart) {
        throw new Error('Dialogue has no part — create one in Material Management.');
      }
      for (let i = 0; i < audioIds.length; i++) {
        await adminDialogueCategoriesAPI.addAudioToDialoguePart(dialogue.id, {
          part_id: firstPart.id,
          audio_file_id: audioIds[i],
          order: i,
        });
      }
      setSuccess('Dialogue added to Dialogue Categories successfully!');
      setDialogueEntries([]);
      setDialoguePreviews(new Map());
      setCategoryDialogueName('');
      fetchLabItems();
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to add dialogue');
    } finally {
      setDialogueSaving(false);
    }
  };

  return (
    <div className="lab-tab">
      {error && (
        <motion.div
          className="lab-alert lab-alert-error"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => setError(null)}
        >
          {error}
        </motion.div>
      )}
      {success && (
        <motion.div
          className="lab-alert lab-alert-success"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => setSuccess(null)}
        >
          {success}
        </motion.div>
      )}

      <div className="lab-output-type">
        <label>Output type:</label>
        <div className="lab-radio-group">
          <button
            type="button"
            className={`lab-radio-btn ${outputType === 'segment' ? 'active' : ''}`}
            onClick={() => setOutputType('segment')}
          >
            Structural Segment
          </button>
          <button
            type="button"
            className={`lab-radio-btn ${outputType === 'dialogue' ? 'active' : ''}`}
            onClick={() => setOutputType('dialogue')}
          >
            Dialogue Categories
          </button>
        </div>
      </div>

      <div className="lab-voice-select">
        <label>Voice:</label>
        <select
          value={selectedVoiceId}
          onChange={(e) => setSelectedVoiceId(e.target.value)}
          disabled={loadingVoices}
        >
          {loadingVoices ? (
            <option>Loading...</option>
          ) : (
            voices.map((v) => (
              <option key={v.voice_id} value={v.voice_id}>
                {v.name}
              </option>
            ))
          )}
        </select>
        <button type="button" className="lab-refresh-btn" onClick={fetchVoices} disabled={loadingVoices}>
          {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number; className?: string }>, { size: 16, className: loadingVoices ? 'spinner' : '' })}
        </button>
      </div>

      {outputType === 'segment' && (
        <motion.div className="lab-segment-form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <h3>Structural Segment (English)</h3>
          <div className="lab-form-group">
            <label>Text (English)</label>
            <textarea
              value={segmentText}
              onChange={(e) => setSegmentText(e.target.value)}
              placeholder="Enter English text..."
              rows={4}
            />
          </div>
          <div className="lab-form-group">
            <label>Display name (for storage)</label>
            <input
              type="text"
              value={segmentName}
              onChange={(e) => setSegmentName(e.target.value)}
              placeholder="e.g. segment_intro"
            />
          </div>
          <div className="lab-actions">
            <button
              type="button"
              className="lab-btn lab-btn-generate"
              onClick={handleGenerateSegment}
              disabled={!segmentText.trim() || !selectedVoiceId || segmentGenerating}
            >
              {segmentGenerating ? (
                <>{React.createElement(FiRefreshCw as React.ComponentType<{ size?: number; className?: string }>, { size: 16, className: 'spinner' })} Generating...</>
              ) : (
                <>{React.createElement(FiZap as React.ComponentType<{ size?: number }>, { size: 16 })} Generate</>
              )}
            </button>
          </div>
          {segmentPreviewB64 && (
            <div className="lab-preview-block">
              <h4>Preview</h4>
              <audio controls src={base64ToBlobUrl(segmentPreviewB64)} />
              <div className="lab-preview-actions">
                <input
                  type="text"
                  value={segmentName}
                  onChange={(e) => setSegmentName(e.target.value)}
                  placeholder="Name for file"
                />
                <button
                  type="button"
                  className="lab-btn lab-btn-save"
                  onClick={handleAddSegmentToLab}
                  disabled={!segmentName.trim() || segmentSaving}
                >
                  {segmentSaving ? React.createElement(FiRefreshCw as React.ComponentType<{ size?: number; className?: string }>, { size: 16, className: 'spinner' }) : <>{React.createElement(FiCheck as React.ComponentType<{ size?: number }>, { size: 16 })} Add to Lab</>}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {outputType === 'dialogue' && (
        <motion.div className="lab-dialogue-form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <h3>Dialogue Categories</h3>
          <div className="lab-form-group">
            <label>First item language (alternates)</label>
            <div className="lab-radio-group">
              <button
                type="button"
                className={`lab-radio-btn ${dialogueFirstLang === 'en' ? 'active' : ''}`}
                onClick={() => setDialogueFirstLang('en')}
              >
                English
              </button>
              <button
                type="button"
                className={`lab-radio-btn ${dialogueFirstLang === 'fa' ? 'active' : ''}`}
                onClick={() => setDialogueFirstLang('fa')}
              >
                Persian
              </button>
            </div>
          </div>
          <button type="button" className="lab-btn lab-btn-add" onClick={addDialogueEntry}>
            {React.createElement(FiPlus as React.ComponentType<{ size?: number }>, { size: 16 })} Add entry
          </button>
          <div className="lab-dialogue-entries">
            {dialogueEntries.map((entry, idx) => (
              <div key={idx} className="lab-dialogue-entry">
                <div className="lab-entry-header">
                  <span className="lab-entry-lang">{entry.lang === 'en' ? 'EN' : 'FA'}</span>
                  <button
                    type="button"
                    className="lab-remove-btn"
                    onClick={() => removeDialogueEntry(idx)}
                  >
                    {React.createElement(FiTrash2 as React.ComponentType<{ size?: number }>, { size: 14 })}
                  </button>
                </div>
                <textarea
                  value={entry.text}
                  onChange={(e) => updateDialogueEntry(idx, e.target.value)}
                  placeholder={entry.lang === 'en' ? 'English text...' : 'متن فارسی...'}
                  rows={2}
                />
                <div className="lab-entry-actions">
                  <button
                    type="button"
                    className="lab-btn lab-btn-sm"
                    onClick={() => handleGenerateDialogueEntry(idx)}
                    disabled={!entry.text.trim() || dialogueGenerating !== null}
                  >
                    {dialogueGenerating === idx ? React.createElement(FiRefreshCw as React.ComponentType<{ size?: number; className?: string }>, { size: 14, className: 'spinner' }) : 'Generate'}
                  </button>
                  {dialoguePreviews.has(idx) && (
                    <audio controls src={base64ToBlobUrl(dialoguePreviews.get(idx)!)} />
                  )}
                </div>
              </div>
            ))}
          </div>
          {dialogueEntries.length > 0 && (
            <div className="lab-dialogue-add-block">
              <h4>Add as one category dialogue</h4>
              <p className="lab-dialogue-hint">Generate all entries above, then add the full dialogue to a Dialogue Category.</p>
              <div className="lab-form-row">
                <div className="lab-form-group">
                  <label>Category</label>
                  <select
                    value={dialogueCategoryId}
                    onChange={(e) => setDialogueCategoryId(e.target.value ? parseInt(e.target.value) : '')}
                  >
                    <option value="">— Select category —</option>
                    {dialogueCategories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="lab-form-group">
                  <label>Dialogue name</label>
                  <input
                    type="text"
                    value={categoryDialogueName}
                    onChange={(e) => setCategoryDialogueName(e.target.value)}
                    placeholder="e.g. Doctor Appointment"
                  />
                </div>
              </div>
              <button
                type="button"
                className="lab-btn lab-btn-save lab-btn-full"
                onClick={handleAddFullDialogueToLab}
                disabled={
                  dialogueSaving ||
                  dialogueEntries.some((_, i) => !dialoguePreviews.has(i)) ||
                  !categoryDialogueName.trim() ||
                  !dialogueCategoryId
                }
              >
                {dialogueSaving ? React.createElement(FiRefreshCw as React.ComponentType<{ size?: number; className?: string }>, { size: 16, className: 'spinner' }) : <>{React.createElement(FiCheck as React.ComponentType<{ size?: number }>, { size: 16 })} Add to Lab (all entries as one dialogue)</>}
              </button>
            </div>
          )}
        </motion.div>
      )}

      <div className="lab-items-section">
        <h3>Lab Items ({labItems.length})</h3>
        <p className="lab-items-hint">Use &quot;Select from Lab&quot; when adding audio to segments or category dialogues.</p>
        {labItems.length === 0 ? (
          <p className="lab-empty">No lab items yet. Generate and add above.</p>
        ) : (
          <div className="lab-items-list">
            {labItems.map((item) => (
              <div key={item.id} className="lab-item-card">
                {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 18 })}
                <span>{item.file_name}</span>
                <button
                  type="button"
                  className="lab-item-edit-content"
                  onClick={() => setEditingContentAudioId(item.id)}
                  title="Edit source text and translations"
                >
                  {React.createElement(FiEdit2 as React.ComponentType<{ size?: number }>, { size: 14 })}
                  Content
                </button>
              </div>
            ))}
          </div>
        )}
        {editingContentAudioId && (
          <AudioContentEditor
            audioId={editingContentAudioId}
            audioFileName={labItems.find((i) => i.id === editingContentAudioId)?.file_name || ''}
            onClose={() => setEditingContentAudioId(null)}
            onSaved={() => setEditingContentAudioId(null)}
          />
        )}
      </div>
    </div>
  );
};

export default LabTab;
